import React from "react";
import { Routes, Route, NavLink, Link } from "react-router-dom";
import {
  LayoutDashboard, FileCheck2, GraduationCap, Laptop, ShieldCheck,
  Building2, AlertTriangle, Siren, FileWarning, ArrowLeft, ShieldAlert,
} from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import ThemeToggle from "../components/ThemeToggle";
import { useAuth } from "../context/AuthContext";
import { cn } from "../lib/utils";

import PortalDashboard from "../components/portal/PortalDashboard";
import PortalPolicies from "../components/portal/PortalPolicies";
import PortalTrainings from "../components/portal/PortalTrainings";
import PortalDevices from "../components/portal/PortalDevices";
import PortalMfa from "../components/portal/PortalMfa";
import PortalVendorRequests from "../components/portal/PortalVendorRequests";
import PortalRisks from "../components/portal/PortalRisks";
import PortalIncidents from "../components/portal/PortalIncidents";
import PortalExceptions from "../components/portal/PortalExceptions";
import PortalAdminReview from "../components/portal/PortalAdminReview";

const TABS = [
  { to: "/portal", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/portal/policies", label: "Policies", icon: FileCheck2 },
  { to: "/portal/trainings", label: "Trainings", icon: GraduationCap },
  { to: "/portal/devices", label: "Devices", icon: Laptop },
  { to: "/portal/mfa", label: "MFA", icon: ShieldCheck },
  { to: "/portal/vendor-requests", label: "Vendor Requests", icon: Building2 },
  { to: "/portal/risks", label: "Risks", icon: AlertTriangle },
  { to: "/portal/incidents", label: "Incidents", icon: Siren },
  { to: "/portal/exceptions", label: "Exceptions", icon: FileWarning },
];

export default function EmployeePortal() {
  const { user } = useAuth();
  const isAdmin = user?.role === "Super Admin" || (Array.isArray(user?.scopes) && user.scopes.includes("administrator"));
  const tabs = isAdmin ? [...TABS, { to: "/portal/admin-review", label: "Admin Review", icon: ShieldAlert }] : TABS;

  return (
    <div className="min-h-screen bg-background" data-testid="employee-portal-shell">
      <header className="border-b border-border bg-card">
        <div className="mx-auto max-w-[1200px] px-4 sm:px-6 h-20 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <BrandLogo variant="full" className="h-12 w-auto" />
            <div className="hidden sm:block h-8 w-px bg-border" />
            <span className="hidden sm:inline text-[13px] font-semibold text-muted-foreground tracking-wide">Employee Portal</span>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-lg border border-border text-[13px] font-medium hover:bg-muted transition-colors"
              data-testid="portal-back-to-admin-link"
            >
              <ArrowLeft className="w-4 h-4" /> Back to UC Vigil
            </Link>
          </div>
        </div>
        <nav className="mx-auto max-w-[1200px] px-4 sm:px-6 flex gap-1 overflow-x-auto" data-testid="employee-portal-nav">
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={`portal-tab-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => cn(
                "flex items-center gap-1.5 px-3.5 h-11 text-[13px] font-medium whitespace-nowrap border-b-2 transition-colors",
                isActive ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-[1200px] px-4 sm:px-6 py-6">
        <Routes>
          <Route index element={<PortalDashboard />} />
          <Route path="policies" element={<PortalPolicies />} />
          <Route path="trainings" element={<PortalTrainings />} />
          <Route path="devices" element={<PortalDevices />} />
          <Route path="mfa" element={<PortalMfa />} />
          <Route path="vendor-requests" element={<PortalVendorRequests />} />
          <Route path="risks" element={<PortalRisks />} />
          <Route path="incidents" element={<PortalIncidents />} />
          <Route path="exceptions" element={<PortalExceptions />} />
          {isAdmin && <Route path="admin-review" element={<PortalAdminReview />} />}
        </Routes>
      </main>

      <footer className="mx-auto max-w-[1200px] px-4 sm:px-6 py-8 text-[12px] text-muted-foreground">
        Signed in as {user?.name} ({user?.email})
      </footer>
    </div>
  );
}
